<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue'
import BayInput from '@/components/features/batches/form-elements/BayInput.vue'
import NotesInput from '@/components/features/batches/form-elements/NotesInput.vue'
import LastEditedByInput from '@/components/features/batches/form-elements/LastEditedByInput.vue'
import TabPanel from 'primevue/tabpanel'
import TabView from 'primevue/tabview'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import tabviewStyle2 from '@/presets/lara/tabview/tabviewStyle2'
import { Icon } from '@iconify/vue'
import { useToast } from 'primevue/usetoast'

import type { FinishedBatch } from '@/types/batch-types'
import QualityStatusDropdown from '@/components/features/batches/form-elements/QualityStatusDropdown.vue'
import FormCalendar from '@/components/inputs/FormCalendar.vue'
import FireTestingStatusDropdown from '@/components/features/batches/form-elements/FireTestingStatusDropdown.vue'
import { addFireTest, updateFireTest, addQualityCheck, getFireTestCertificate } from '@/api/batches'
import DateReturnedInput from '@/components/features/batches/form-elements/DateReturnedInput.vue'
import ReturnSupplierInput from '@/components/features/batches/form-elements/ReturnSupplierInput.vue'
import TotalWeightInput from '@/components/features/batches/form-elements/TotalWeightInput.vue'
import TotalLengthInput from '@/components/features/batches/form-elements/TotalLengthInput.vue'
import FinishedEditRollsList from '../form-elements/FinishedEditRollsList.vue'
import FileUploadInput from '@/components/inputs/FileUpload.vue'
import Dialog from 'primevue/dialog'
import type { FireTestSupplier, QualityCheckSupplier } from '@/types/supplier-types'
import { getFireTestSuppliers, getQualityCheckSuppliers } from '@/api/suppliers'
import Dropdown from 'primevue/dropdown'
import type { ErrorResponse } from '@/types/error-types'
import TotalLengthUnit from '@/components/features/batches/form-elements/TotalLengthUnit.vue'
import { type Product } from '@/types/product-types'
import { getProductList } from '@/api/products'
import MachineNumberInput from '@/components/features/batches/form-elements/MachineNumberInput.vue'
import FormInputText from '@/components/inputs/FormInputText.vue'

const props = defineProps<{
  disabled: boolean
  type: 'add' | 'view'
  parent?: string
  modelValue: string
}>()
const emit = defineEmits(['update:modelValue'])
const activeTabIndex = ref(0)
const saveTabIndex = ref<string | null>('0')
const fireTestSuppliers = ref<FireTestSupplier[]>([])
const qualityCheckSuppliers = ref<QualityCheckSupplier[]>([])
const uploadedFile = ref<File | null>(null)
const existingCertificateFile = ref<File | null>(null);
const selectedSupplierName = ref<string | null>(props.modelValue)

const fetchSuppliers = async () => {
  fireTestSuppliers.value = await getFireTestSuppliers()
  qualityCheckSuppliers.value = await getQualityCheckSuppliers();
}

const model = defineModel<FinishedBatch>({
  required: true
})
const toast = useToast()

const isDispatched = computed(() => props.parent === 'dispatched')

const showToast = (type: 'success' | 'error', title: string, message: string) => {
  toast.add({
    severity: type,
    summary: title,
    detail: message,
    life: 5000
  })
}

watch(
  () => props.modelValue,
  (newValue) => {
    selectedSupplierName.value = newValue
  }
)

const batchStatus = computed(() => {
  if (model.value.qualityChecks && model.value.qualityChecks.length > 0) {
    const status = model.value.qualityChecks[0].qualityCheckStatus

    switch (status) {
      case 0:
        return 'Pending'
      case 1:
        return 'Passed'
      case 2:
        return 'Quarantine'
      default:
        return 'Pending'
    }
  }
  return 'Pending'
})

const batchFireTestingStatus = computed(() => {
  if (model.value.fireTests && model.value.fireTests.length > 0) {
    const status = model.value.fireTests[0].fireTestingStatus

    switch (status) {
      case 0:
        return 'Pending'
      case 1:
        return 'Passed'
      case 2:
        return 'Failed'
      default:
        return 'Pending'
    }
  }
  return 'Pending'
})


const batchStatusTag = computed(() => {
  if (model.value.qualityChecks && model.value.qualityChecks.length > 0) {
    const status = model.value.qualityChecks[0].qualityCheckStatus

    switch (status) {
      case 0:
        return 'warning'
      case 1:
        return 'success'
      case 2:
        return 'danger'
      default:
        return ''
    }
  }
  return ''
})

const batchFireTestingStatusTag = computed(() => {
  if (model.value.fireTests && model.value.fireTests.length > 0) {
    const status = model.value.fireTests[0].fireTestingStatus

    switch (status) {
      case 0:
        return 'warning'
      case 1:
        return 'success'
      case 2:
        return 'danger'
      default:
        return ''
    }
  }
  return ''
})

const showFileModal = ref(false)
const fileUrl = ref('')
const fileType = ref('')

const viewCertificate = async (fireTestId: string) => {
  const blob = await getFireTestCertificate(fireTestId)
  fileUrl.value = window.URL.createObjectURL(blob)
  fileType.value = blob.type
  showFileModal.value = true
  activeTabIndex.value = 2
  localStorage.setItem('activeTabIndex', '2')
}

const onModalClose = () => {
  window.URL.revokeObjectURL(fileUrl.value)
  fileUrl.value = ''
  fileType.value = ''

  activeTabIndex.value = 0
  saveTabIndex.value = '0'
  localStorage.setItem('activeTabIndex', '0');
}

const handlePostQualityCheck = async () => {
  if (!model.value.id || !model.value.lastEditedBy) {
    showToast('error', 'Error', 'Missing required fields');
    return;
  }

  if (!model.value.qualityChecks[0].qualityCheckSupplierId) {
    showToast('error', 'Required Field', 'The Tester dropdown is required.');
    return;
  }

  if (!model.value.qualityChecks[0].dateCarriedOut) {
    showToast('error', 'Required Field', 'The date is required.');
    return;
  }

  const batchState = model.value.qualityChecks[0].qualityCheckStatus

  const formatedData = {
    returnedBatchId: model.value.id,
    qualityCheckStatus: batchState,
    testerName: model.value.qualityChecks[0].testerName,
    qualityCheckSupplierId: model.value.qualityChecks[0].qualityCheckSupplierId,
    dateCarriedOut: model.value.qualityChecks[0].dateCarriedOut,
    notes: model.value.qualityChecks[0].notes,
    lastEditedBy: model.value.lastEditedBy,
    rollIds: model.value.qualityChecks[0].rollIds ?? []
  }

  await addQualityCheck(formatedData)
    .then(() => {
      showToast('success', 'Success', 'Quality Check Added Successfully')
    })
    .catch((error) => {
      console.error(error)
      showToast('error', 'Error', error.response.data.message)
    })
}

const isExistingTest = computed(() => model.value.fireTests?.some(ft => !!ft.id))

const handleSaveOrEdit = async () => {
  const fireTest = model.value.fireTests?.[0];

  if (!fireTest) {
    showToast('error', 'Error', 'No fire test data available.');
    return;
  }

  if (!fireTest.fireTestSupplierId) {
    showToast('error', 'Required Field', 'The Tester dropdown is required.');
    return;
  }

  if (!fireTest.dateCarriedOut) {
    showToast('error', 'Required Field', 'The Test Date is required.');
    return;
  }

  const fileToUpload = uploadedFile.value ?? (isExistingTest.value ? existingCertificateFile.value : null);

  if (!fileToUpload) {
    showToast('error', 'File Missing', 'A certificate file is required.');
    return;
  }

  const formData = new FormData();
  formData.append('FireTestCertificateFile', fileToUpload);

  formData.append('FireTestingStatus', fireTest.fireTestingStatus.toString());
  formData.append('TestType', fireTest.testType ?? '');
  formData.append('DateCarriedOut', new Date(fireTest.dateCarriedOut).toISOString());
  formData.append('Notes', fireTest.notes);

  try {
    if (isExistingTest.value) {
      formData.append('ReturnBatchId', model.value.id as string);
      formData.append('FreTestSupplierId', fireTest.fireTestSupplierId);

      await updateFireTest(fireTest.id!, formData);
      showToast('success', 'Success', 'Fire Test Updated Successfully');

    } else {
      formData.append('ReturnedBatchId', model.value.id as string);
      formData.append('FireTestSupplierId', fireTest.fireTestSupplierId);

      var newFireTest = await addFireTest(formData);
      showToast('success', 'Success', 'Fire Test Added Successfully');

      if(!model.value.fireTests)
      {
        model.value.fireTests = []
      }
      model.value.fireTests[0] = newFireTest
    }

  } catch (error: any) {
    console.error("Could not fetch existing certificate:", error);
    showToast('error', 'Error', 'Could not load existing certificate file.');  }
};

onBeforeMount(() => {
  if (!model.value.qualityChecks || model.value.qualityChecks.length === 0) {
    model.value.qualityChecks = [
      {
        qualityCheckStatus: 0,
        testerName: '',
        qualityCheckSupplierId: '',
        dateCarriedOut: '',
        notes: '',
        rollIds: [],
        returnedBatchId: model.value.id
      }
    ]
  }
  if (!model.value.fireTests || model.value.fireTests.length === 0) {
    model.value.fireTests = [
      {
        fireTestingStatus: 0,
        fireTestSupplierId: '',
        testType: '',
        dateCarriedOut: '',
        notes: '',
        returnedBatchId: model.value.id,
        fireTestCertificateFile: null
      }
    ]
  }
})

const qualities = ref<Product[]>()
const qualityName = computed(() => {
  return (
    qualities.value?.find((quality) => quality.id == model.value.finishingQualityId)
      ?.finishedReference || 'Unknown Quality'
  )
})

// Inside onMounted
onMounted(async () => {
  fetchSuppliers();
  qualities.value = await getProductList();

  if (isExistingTest.value && model.value.fireTests?.[0]?.id) {
    try {
      const fireTestId = model.value.fireTests[0].id;
      const blob = await getFireTestCertificate(fireTestId);
      existingCertificateFile.value = new File([blob], "existing-certificate", { type: blob.type });
    } catch (error) {
      console.error("Could not fetch existing certificate:", error);
      showToast('error', 'Error', 'Could not load existing certificate file.');
    }
  }

  setTimeout(() => {
  }, 100);
});

watch(uploadedFile, (newFile) => {
  if (newFile) {
    activeTabIndex.value = 2;
    localStorage.setItem('activeTabIndex', '2');
  }
});
</script>

<template>
  <div class="grid grid-cols-12 gap-20 gap-y-4">
    <TabView
      :pt="tabviewStyle2"
      :pt-options="{
        mergeProps: false,
        mergeSections: false
      }"
      v-model:active-index="activeTabIndex"
      class="col-span-12"
    >
      <TabPanel header="Batch Details">
        <h2 class="font-semibold text-lg my-5">Batch Details</h2>

        <div class="grid grid-cols-12 gap-20 gap-y-4">
          <div class="flex col-span-12 items-center gap-5">
            <span class="text-sm">Rolls in Batch ({{ model.returnedRolls?.length }})</span>
            <RouterLink
                :to="`/productSpecification/${model.finishingQualityId}`"
                class="flex gap-2 items-center bg-green-100 text-green-600 py-1 px-3 rounded-lg hover:bg-green-200 transition-colors"
            >
              <Icon icon="mdi:fabric" class="text-xl" />
              {{ qualityName }}
            </RouterLink>
          </div>
          <FinishedEditRollsList
            v-model="model.returnedRolls"
            class="col-span-12"
            :includeSupplierNo="true"
            lengthLabel="New Length"
            weightLabel="Roll Weight"
            :show-actions="false"
            :show-weight-change="true"
            :current-batch-number="model.batchNumber"
          />

          <DateReturnedInput
            class="col-span-6"
            input-name="dateReturned"
            v-model="model.dateReturned"
            :disabled="disabled"
          />
          <FormCalendar
            v-if="isDispatched"
            id="dateDispatched"
            label="Date Dispatched:"
            input-name="dateDispatched"
            v-model="model.dateDispatched"
            class="col-span-6"
            :disabled="disabled"
          />
          <ReturnSupplierInput
            class="col-span-6"
            input-name="finishingSupplierId"
            v-model="model.finishingSupplierId"
            :disabled="disabled"
          />
          <div class="flex items-center gap-4 w-full col-span-6">
            <TotalLengthInput
              :disabled="true"
              input-name="totalLengthValue"
              v-model="model.totalLengthValue"
            />
            <TotalLengthUnit
              input-name="totalLengthUnit"
              :disabled="true"
              v-model="model.totalLengthUnit"
            />
          </div>
          <div class="col-span-6">
            <TotalWeightInput
              v-model="model.totalWeight"
              input-name="totalWeight"
              :disabled="disabled"
            />
            <div class="mt-2 text-sm">
              <span
              >Weight Change:
                <span
                  :class="
                    model.totalPercentageWeightLostOrGained! > 0 ? 'text-green-600' : 'text-red-600'
                  "
                  class="inline-flex items-center"
                >
                  {{ model.totalPercentageWeightLostOrGained?.toFixed(2) }}%
                  <Icon
                    :icon="
                      model.totalPercentageWeightLostOrGained! > 0
                        ? 'mdi:arrow-up'
                        : 'mdi:arrow-down'
                    "
                    class="ml-1"
                  />
                </span>
              </span>
            </div>
          </div>
          <BayInput class="col-span-6" input-name="bay" v-model="model.bay" :disabled="disabled" />
          <MachineNumberInput class="col-span-6" input-name="machineNumber" v-model="model.machineNumber" :disabled="disabled"/>
          <NotesInput
            class="col-span-12"
            input-name="notes"
            v-model="model.notes"
            :disabled="disabled"
          />
          <LastEditedByInput class="col-span-6" />
        </div>
      </TabPanel>
      <TabPanel header="Quality Check" :disabled="type === 'add'">
        <div class="flex justify-between items-center">
          <h2 class="font-semibold text-lg my-5">
            Quality Check
            <Tag :severity="batchStatusTag" class="!px-3">{{ batchStatus }}</Tag>
          </h2>

          <Button
            v-if="parent !== 'available'"
            outlined
            label="Save"
            @click="handlePostQualityCheck"
          />
        </div>

        <div class="grid grid-cols-12 gap-20 gap-y-4">
          <QualityStatusDropdown
            class="col-span-6"
            input-name="batchTestingStatus"
            v-model="model.qualityChecks[0].qualityCheckStatus"
            :disabled="parent == 'available'"
          />
          <div class="col-span-6 flex flex-col gap-2">
            <label for="testerName">Tester:</label>
            <Dropdown
              class="custom-dropdown flex-1"
              input-name="testerName"
              v-model="model.qualityChecks[0].qualityCheckSupplierId"
              :options="qualityCheckSuppliers"
              option-label="name"
              option-value="id"
              :disabled="parent == 'available'"
              placeholder="Select a tester"
            />
          </div>
          <div class="col-span-6 flex flex-col gap-2">
            <label for="testerName">Tester Name:</label>
            <FormInputText
                id="testerName"
                input-name="testerName"
                v-model="model.qualityChecks[0].testerName"
                :view-only="parent == 'available'"
            />
          </div>
          <FormCalendar
            id="dateCarriedOut"
            label="Date:"
            input-name="dateCarriedOut"
            v-model="model.qualityChecks[0].dateCarriedOut"
            class="col-span-6"
            :disabled="parent == 'available'"
          />
          <NotesInput
            class="col-span-12"
            input-name="qualityCheckNotes"
            v-model="model.qualityChecks[0].notes"
            :disabled="parent == 'available'"
          />
          <LastEditedByInput
            class="col-span-6"
            input-name="lastEditedBy"
            v-model="model.lastEditedBy"
            :disabled="parent == 'available'"
          />
        </div>
      </TabPanel>
      <TabPanel :disabled="type === 'add'" header="FR Testing">
        <div class="flex justify-between items-center">
          <h2 class="font-semibold text-lg my-5">
            FR Testing
            <Tag :severity="batchFireTestingStatusTag" class="!px-3">{{
                batchFireTestingStatus
              }}</Tag>
          </h2>

          <div class="flex gap-4 items-center">
            <!-- "View Certificate" button only shows if the test exists -->
            <Button
              v-if="isExistingTest"
              outlined
              icon="pi pi-eye"
              label="View Certificate"
              @click="viewCertificate(model.fireTests[0].id!)"
            />

            <!-- The file upload input is always available for adding or updating -->
            <FileUploadInput v-model="uploadedFile" v-if="parent !== 'available'" />

            <!-- The main action button -->
            <Button
              v-if="parent !== 'available'"
              outlined
              label="Save"
              @click="handleSaveOrEdit"
            />
          </div>
        </div>

        <div class="grid grid-cols-12 gap-20 gap-y-4">
          <FireTestingStatusDropdown
            class="col-span-6"
            input-name="batchTestingStatus"
            v-model="model.fireTests[0].fireTestingStatus"
            :disabled="parent == 'available'"
          />
          <div class="col-span-6 flex flex-col gap-2">
            <label for="testerName">Tester:</label>
            <Dropdown
              class="custom-dropdown flex-1"
              input-name="testerName"
              v-model="model.fireTests[0].fireTestSupplierId"
              :options="fireTestSuppliers"
              option-label="name"
              option-value="id"
              :disabled="parent == 'available'"
              placeholder="Select a tester"
            />
          </div>
          <FormCalendar
            id="dateCarriedOut"
            label="Test Date:"
            input-name="dateCarriedOut"
            v-model="model.fireTests[0].dateCarriedOut"
            class="col-span-6"
            :disabled="parent == 'available'"
          />
          <!-- <SupplierInput
            class="col-span-6"
            input-name="supplier"
            label="Supplier (Test House):"
            placeholder="Choose tester from suppliers"
          /> -->
          <NotesInput
            class="col-span-12"
            input-name="notes"
            v-model="model.fireTests[0].notes"
            :disabled="parent == 'available'"
          />
          <LastEditedByInput
            class="col-span-6"
            v-model="model.lastEditedBy"
            :disabled="parent == 'available'"
          />
        </div>
      </TabPanel>
    </TabView>
  </div>
  <Dialog
    v-model:visible="showFileModal"
    modal
    header="Fire Test Certificate"
    :style="{ width: '80vw' }"
    @hide="onModalClose"
  >
    <img
      v-if="fileUrl && fileType.includes('image')"
      :src="fileUrl"
      style="width: 100%; height: auto"
      alt="Fire Test Certificate"
    />
    <iframe
      v-else-if="fileUrl && fileType.includes('pdf')"
      :src="fileUrl"
      width="100%"
      height="600px"
    />
    <p v-else>Unsupported file type</p>
  </Dialog>
</template>

<style scoped>
.custom-dropdown {
  height: 50px;
}
</style>